import { useEffect, useState } from 'react';

import type { MutationHookOptions, MutationResult } from '@main/core-ui';
import { buildUseMutation } from '@main/core-ui';
import type {
  GqlObject,
  MutationEndpoint,
  ParamsToType,
  ResponseToType,
} from '@main/schema-utils';
import type { DiffieHellmanMutate } from '@main/sm-browser';
import { wrapMutateWithDiffieHellman } from '@main/sm-browser';
import type {
  DiffieHellmanParams,
  DiffieHellmanResponse,
} from '@main/sombra-types';
import { isDiffieHellmanError } from '@main/sombra-types';

import { useCheckSombraScopesError } from './useCheckSombraScopesError';
import { useDiffieHellman } from './useDiffieHellman';
import { useLogout } from './useLogout';

/**
 * A GraphQL mutation with diffie hellman support built in
 *
 * @param endpoint - The GraphQL mutation endpoint definition (requires dhEncrypted as input)
 * @param operationName - The GraphQL operation name
 * @param responseFields - Return a partial response
 * @returns A hook that calls useApolloMutation but adds type enforcements
 */
export function buildUseDhMutation<
  TName extends string,
  TParams extends DiffieHellmanParams,
  TResult extends DiffieHellmanResponse,
>(
  endpoint: MutationEndpoint<TName, TParams, TResult>,
  operationName?: string,
  responseFields?: GqlObject<TResult>,
  // TODO: https://github.com/transcend-io/main/issues/6322 - this response type should be partial if responseFields is provided
): (
  options?: MutationHookOptions<
    ResponseToType<TResult>,
    Omit<ParamsToType<TParams>, 'dhEncrypted'>
  >,
) => [
  undefined | DiffieHellmanMutate<TParams, TResult>,
  MutationResult<ResponseToType<TResult>> & {
    /** The unwrapped response from the diffie hellman channel */
    unwrappedResponse?: Buffer;
  },
] {
  // Create the raw hook using the core `buildUseMutation`
  const useMutation = buildUseMutation(endpoint, operationName, responseFields);

  // Wrap the hook with diffie hellman support
  return (options: any) => {
    /** Store the decrypted response sent back after result */
    const [unwrappedResponse, setUnwrappedResponse] = useState<Buffer>();
    const logout = useLogout();
    const checkSombraScopesError = useCheckSombraScopesError();

    /** The raw mutation */
    const [mutate, mutateResponse] = useMutation(options as any); // dhEncryption-less options are hard to type
    const { error } = mutateResponse;

    /** Diffie hellman support */
    const [createDhEncrypted, decryptDhResponse] = useDiffieHellman();

    /** Log out of the privacy center when session expires */
    useEffect(() => {
      if (error?.message) {
        if (isDiffieHellmanError(error.message)) {
          logout();
        }
        checkSombraScopesError(error.message);
      }
    }, [error]);

    return [
      !createDhEncrypted
        ? undefined
        : wrapMutateWithDiffieHellman<any, any>(
            mutate as any,
            createDhEncrypted,
            decryptDhResponse,
            setUnwrappedResponse,
          ),
      { unwrappedResponse, ...mutateResponse },
    ] as any;
  };
}
